import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userSite, setUserSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const axiosAuth = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axiosAuth.get('/auth/me');
      setUser(response.data.user);
      setUserSite(response.data.userSite);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, userSite: userSiteData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setUserSite(userSiteData);
    return response.data;
  };

  const signup = async (email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUserSite(null);
  };

  const updatePreferences = async (wantsUpdates) => {
    await axiosAuth.put('/auth/preferences', { wantsUpdates });
    setUserSite(prev => ({ ...prev, wantsUpdates }));
  };

  const resendVerification = async () => {
    return await axiosAuth.post('/auth/resend-verification');
  };

  const isAdmin = userSite?.role === 'owner' || userSite?.role === 'admin' || userSite?.role === 'editor';

  return (
    <AuthContext.Provider value={{
      user,
      userSite,
      loading,
      token,
      login,
      signup,
      logout,
      updatePreferences,
      resendVerification,
      isAdmin,
      isVerified: user?.isVerified
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
